import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { ChevronDown, X } from "lucide-react";
import "./ComboBox.css";

export interface ComboOption {
  value: string;
  label: string;
}

/**
 * A searchable single-select combobox — the React parity for the Jmix/Vaadin `ComboBox` that the
 * intake wizard's Diersoort/Ras/Vacht/Kleur fields used: click (or focus) to open, type to filter the
 * options by case-insensitive substring, arrow-key + Enter to pick, a clear (×) affordance, Escape to
 * close. Reuses the `.fui-field` label vocabulary so it lines up with the plain `Field`/`SelectField`
 * controls around it.
 *
 * Page-local to the wizards on purpose (StepWizard rule): promote into `@fawkes/ui` only when a third
 * consumer shows up. Kept a controlled component — `value` is the selected option's `value` ("" =
 * nothing selected); `onChange` receives the new value.
 */
export function ComboBox({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  emptyLabel = "Geen resultaten",
  invalid,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Text shown when the typed query matches no option. */
  emptyLabel?: string;
  invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // Open upward when there isn't enough room below; cap the list height to the available space.
  const [dropUp, setDropUp] = useState(false);
  const [listMax, setListMax] = useState<number | undefined>(undefined);
  // The list renders position:FIXED (the Jmix reparent-to-<body> idiom) with coordinates taken from
  // the control's bounding rect, so the scrolling modal body cannot clip it.
  const [listPos, setListPos] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const listId = useId();
  const inputId = useId();

  const selected = options.find((o) => o.value === value) ?? null;

  // The reference data has duplicate rows per label (e.g. several "Zwart"/"Beige-wit-zwart" colours);
  // show each label only once in the dropdown (Jmix parity). The selected display still resolves
  // against the full `options`, and search matches by label, so keeping any one id per label is fine.
  const dedupedOptions = useMemo(() => {
    const seen = new Set<string>();
    return options.filter((o) => {
      const key = o.label.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [options]);

  // Substring filter while typing; the full (deduped) list when the query is empty.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return dedupedOptions;
    // Substring match (Jmix), but rank the options that START with the query first — typing "zw" must
    // offer "Zwart" before "Beige-wit-zwart".
    const hits = dedupedOptions.filter((o) => o.label.toLowerCase().includes(q));
    return hits.sort((a, b) => {
      const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
      return aStarts !== bStarts ? aStarts - bStarts : a.label.localeCompare(b.label);
    });
  }, [dedupedOptions, query]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Keep the highlighted row within the (possibly shrunk) filtered list.
  useEffect(() => {
    setActive((a) => Math.min(Math.max(a, 0), Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  // Scroll the highlighted row into view as it moves.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function openList() {
    if (disabled || open) return;
    // Decide drop direction from the room below vs above the control (the bottom fields of a modal
    // don't have room below, so the list would otherwise be clipped) and clamp its height to fit.
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      const margin = 12;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      const up = spaceBelow < 240 && spaceAbove > spaceBelow;
      setDropUp(up);
      setListMax(Math.min(240, Math.max(120, Math.floor(up ? spaceAbove : spaceBelow))));
      setListPos({
        left: rect.left,
        width: rect.width,
        ...(up ? { bottom: window.innerHeight - rect.top + 2 } : { top: rect.bottom + 2 }),
      });
    }
    setOpen(true);
    setQuery("");
    const idx = dedupedOptions.findIndex((o) => o.value === value);
    setActive(idx >= 0 ? idx : 0);
  }

  function choose(opt: ComboOption) {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) choose(filtered[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
    }
  }

  return (
    <div className={"fui-field fawkes-combo" + (open ? " fawkes-combo--open" : "")} ref={rootRef}>
      {/* A real <label htmlFor>, not a <span>: the control is a bare input, so without the binding it
          reaches assistive tech (and getByLabelText) with no accessible name at all. */}
      <label className="fui-field__label" htmlFor={inputId}>{label}</label>
      <div className="fawkes-combo__control">
        <input
          id={inputId}
          ref={inputRef}
          className={"fui-field__input fawkes-combo__input" + (invalid ? " fawkes-combo__input--invalid" : "")}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={selected ? selected.label : placeholder}
          value={open ? query : selected?.label ?? ""}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={openList}
          onMouseDown={() => openList()}
          onKeyDown={onKeyDown}
          // Typing scrolls a long value out of view; browsers keep that scroll on blur, so a picked
          // "Labrador Retriever" would sit there reading "rador Retriever". Rewind to the start, and
          // the ellipsis does its job.
          onBlur={(e) => {
            e.currentTarget.scrollLeft = 0;
          }}
        />
        {selected && !disabled ? (
          <button
            type="button"
            className="fawkes-combo__clear"
            aria-label="Wissen"
            tabIndex={-1}
            onMouseDown={(e) => {
              e.preventDefault();
              onChange("");
              setQuery("");
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        ) : null}
        <span className="fawkes-combo__chevron" aria-hidden="true">
          <ChevronDown size={16} />
        </span>
      </div>
      {open ? (
        <ul
          className={"fawkes-combo__list" + (dropUp ? " fawkes-combo__list--up" : "")}
          id={listId}
          role="listbox"
          ref={listRef}
          style={{
            ...(listMax != null ? { maxHeight: listMax } : null),
            ...(listPos
              ? {
                  position: "fixed" as const,
                  left: listPos.left,
                  width: listPos.width,
                  ...(listPos.top != null ? { top: listPos.top } : { bottom: listPos.bottom }),
                }
              : null),
          }}
        >
          {filtered.length === 0 ? (
            <li className="fawkes-combo__empty">{emptyLabel}</li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={
                  "fawkes-combo__option" +
                  (i === active ? " fawkes-combo__option--active" : "") +
                  (o.value === value ? " fawkes-combo__option--selected" : "")
                }
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
